import React from 'react'
import { __ } from '@wordpress/i18n'
import { useStore } from '@nanostores/react'
import PropTypes from 'prop-types'
import { Tooltip } from 'react-tooltip'
import { renderRichText } from '../../../gutenberg/utils'

import Radar from './Radar'
import { activeTile, prev, next, hoverTile } from './store'
import { RadarBlockWrapper } from './custom.styled'

function SegmentDetail({ data, active, index }) {
  return (
    <div
      data-active={active}
      aria-hidden={!active}
      className="segment-details--item"
    >
      {data.icon.url ? (
        <img
          src={data.icon.url}
          alt={data.icon.alt || data.icon.title}
          width={70}
          height={70}
        />
      ) : null}
      <h2>
        {index}. {renderRichText(data.headline, 'span')}
      </h2>
      {renderRichText(data.content, 'p')}
      <div
        className="cta"
        dangerouslySetInnerHTML={{ __html: data.btnLabel }}
      ></div>
    </div>
  )
}

export default function RadarBlock({
  title,
  headline,
  content,
  btnLabel,
  tooltipTitle,
  tooltipScore,
  isEditor,
  tileContents,
  editorIcon,
}) {
  const activeIndex = useStore(activeTile)
  const hoverIndex = useStore(hoverTile)

  return (
    <RadarBlockWrapper className="dhsv-radar-block">
      <h2>
        <mark className="has-inline-color has-secondary-color">_</mark>
        {renderRichText(title, 'span')}
      </h2>
      <div className="container">
        <Radar />
        {isEditor ? (
          <Tooltip
            id="radar-tooltip"
            className="radar-tooltip"
            clickable
            openOnClick={true}
          >
            <p className="radar-tooltip__title">
              {activeIndex}. {renderRichText(tooltipTitle, 'span')}
            </p>
            <p>
              {__('Global score:', 'dhsv_theme')}{' '}
              {renderRichText(tooltipScore, 'span')}
            </p>
          </Tooltip>
        ) : (
          <Tooltip id="radar-tooltip" className="radar-tooltip" float={true}>
            <p className="radar-tooltip__title">
              {hoverIndex}.{' '}
              <span
                dangerouslySetInnerHTML={{
                  __html:
                    tileContents[hoverIndex].tooltipTitle ||
                    tileContents[hoverIndex].headline,
                }}
              ></span>
            </p>
            {tileContents[hoverIndex].tooltipScore && (
              <p>
                {__('Global score:', 'dhsv_theme')}{' '}
                <span className="radar-tooltip__score">
                  {tileContents[hoverIndex].tooltipScore}
                </span>
              </p>
            )}
          </Tooltip>
        )}
        <div className="spacer-decor">
          <svg
            width="27"
            height="430"
            viewBox="0 0 27 430"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path opacity="0.1" d="M1 430L1 239" stroke="#004775" />
            <path opacity="0.1" d="M2 191L2 -3.21865e-06" stroke="#004775" />
            <path
              opacity="0.1"
              d="M1.5 239L26 214.5L1.5 190"
              stroke="#004775"
            />
          </svg>
        </div>
        <div className="segment-details">
          {isEditor ? (
            <div className="segment-details--item" data-active>
              {editorIcon ? (
                <img
                  src={editorIcon.source_url}
                  alt={editorIcon.alt_text}
                  width={70}
                  height={70}
                />
              ) : null}
              <h2>
                {activeIndex}. {renderRichText(headline, 'span')}
              </h2>
              {renderRichText(content, 'p')}
              <div className="cta">{renderRichText(btnLabel, 'a')}</div>
            </div>
          ) : (
            Object.entries(tileContents).map(item => {
              const [key, data] = item

              return (
                <SegmentDetail
                  key={key}
                  data={data}
                  index={key}
                  active={Number(key) === activeIndex}
                />
              )
            })
          )}
        </div>
      </div>
      <div className="button-controls">
        <button onClick={prev} className="button-controls--prev">
          <ButtonChevron />
        </button>
        <button onClick={next}>
          <ButtonChevron />
        </button>
      </div>
    </RadarBlockWrapper>
  )
}

function ButtonChevron() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 40 40"
      width="40"
      height="40"
      focusable="false"
    >
      <path d="m15.5 0.932-4.3 4.38 14.5 14.6-14.5 14.5 4.3 4.4 14.6-14.6 4.4-4.3-4.4-4.4-14.6-14.6z"></path>
    </svg>
  )
}

RadarBlock.propTypes = {
  title: PropTypes.string.isRequired,
  isEditor: PropTypes.bool,
  headline: PropTypes.string,
  tooltipTitle: PropTypes.string,
  tooltipScore: PropTypes.string,
  content: PropTypes.string,
  btnLabel: PropTypes.string,
  tileContents: PropTypes.array,
  editorIcon: PropTypes.object,
}

SegmentDetail.propTypes = {
  active: PropTypes.bool.isRequired,
  data: PropTypes.object.isRequired,
  index: PropTypes.string.isRequired,
}
